import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Drawer,
  FormField,
  Input,
  MoneyInput,
} from '@jumpstone/ui-react';
import type { MinorUnits, PayRunLineItem } from '../../data/types';
import type {
  SetPayRunLineDeltasInput,
  PayRunMutationResult,
} from '../../data/use_pay_runs';

/**
 * Client-side mirror of `apps/api/src/trpc/routers/payroll.ts`'s
 * `setPayRunLineAdjustment` cap on `adjustment_reason`
 * (`z.string().max(500)`). Enforced here so a too-long reason never
 * round-trips through the generic save-failed error.
 */
const MAX_REASON_LENGTH = 500;

/**
 * Per-line adjustment / lump-sum editor for the wizard's Review Pay step
 * (#23309). Renders in a slide-out `<Drawer>` so it composes naturally with
 * the table below and keeps the shell page in view.
 *
 * Both fields are optional edits: the drawer decides for each one whether
 * the user actually changed it and asks {@link setPayRunLineDeltas} to fire
 * zero, one, or two backend mutations accordingly. Passing `null` /
 * `0`-cents for either field explicitly CLEARS the corresponding delta —
 * this is the pre-existing mutation contract; see
 * `setPayRunLineAdjustment` / `setPayRunLineLumpSum`'s own docblocks.
 *
 * Money is stored as integer minor units end-to-end (docs/money-rules.md);
 * the `<MoneyInput>` does the display↔cents conversion in exactly one
 * place, so this component never touches a decimal string.
 */
export function PayRunLineEditDrawer({
  open,
  line,
  pay_run_id,
  onClose,
  onSave,
}: {
  open: boolean;
  line: PayRunLineItem | null;
  pay_run_id: string;
  onClose: () => void;
  onSave: (input: SetPayRunLineDeltasInput) => Promise<PayRunMutationResult>;
}): React.JSX.Element {
  const { t } = useTranslation('wp');

  const initialAdjustment = line?.adjustment_cents ?? 0;
  const initialReason = line?.adjustment_reason ?? '';
  const initialLumpSum = line?.lump_sum_cents ?? 0;

  const [adjustmentCents, setAdjustmentCents] =
    useState<MinorUnits>(initialAdjustment);
  const [adjustmentReason, setAdjustmentReason] =
    useState<string>(initialReason);
  const [lumpSumCents, setLumpSumCents] = useState<MinorUnits>(initialLumpSum);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<'error' | 'step_up' | null>(null);

  // Re-seed the form whenever a different line is opened, or the same drawer
  // is reopened for the same line — otherwise stale state from a previous
  // edit would leak into a fresh open. Mirrors `allowance_drawer.tsx`'s
  // prefill effect.
  useEffect(() => {
    setAdjustmentCents(line?.adjustment_cents ?? 0);
    setAdjustmentReason(line?.adjustment_reason ?? '');
    setLumpSumCents(line?.lump_sum_cents ?? 0);
    setSaving(false);
    setSaveError(null);
  }, [line, open]);

  if (!line) {
    // No line to edit — render nothing. The parent (`ReviewPayStep`)
    // controls `open` on this component and only mounts it once the
    // editable branch is true, so this defensive branch only guards
    // against a caller passing `open` mid-render with `line` briefly
    // resolving; the drawer stays closed then unmounts cleanly.
    return <></>;
  }

  // Capture the narrowed value into a `const` before nested closures capture
  // it — TS doesn't narrow across function boundaries, so `handleSave`
  // would otherwise see `line: PayRunLineItem | null` again.
  const resolvedLine = line;

  const reasonTooLong = adjustmentReason.trim().length > MAX_REASON_LENGTH;
  const canSave = !saving && !reasonTooLong;

  async function handleSave(): Promise<void> {
    setSaving(true);
    setSaveError(null);

    const adjustmentChanged = adjustmentCents !== initialAdjustment;
    const reasonChanged = adjustmentReason.trim() !== initialReason.trim();
    const lumpSumChanged = lumpSumCents !== initialLumpSum;

    // Nothing changed → close without firing any mutation. Prevents a bare
    // "Save" click from making an idempotent-but-still-billable network
    // round trip.
    if (!adjustmentChanged && !reasonChanged && !lumpSumChanged) {
      setSaving(false);
      onClose();
      return;
    }

    const trimmedReason = adjustmentReason.trim();

    const input: SetPayRunLineDeltasInput = {
      pay_run_id,
      employee_id: resolvedLine.employee_id,
    };
    if (adjustmentChanged || reasonChanged) {
      // Explicit 0 clears (mirrors the backend's own `=== 0` → `null`
      // normalization). Send `null` when the input is 0 so the server-side
      // clear branch is taken unambiguously.
      input.adjustment_cents = adjustmentCents === 0 ? null : adjustmentCents;
      input.adjustment_reason =
        input.adjustment_cents === null || trimmedReason === ''
          ? null
          : trimmedReason;
    }
    if (lumpSumChanged) {
      input.lump_sum_cents = lumpSumCents === 0 ? null : lumpSumCents;
    }

    const result = await onSave(input);
    setSaving(false);
    if (result.outcome === 'success') {
      onClose();
      return;
    }
    setSaveError(result.failure === 'step_up_required' ? 'step_up' : 'error');
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('pay_run.line.edit_title', { name: line.employee_name })}
    >
      <div className="flex flex-col gap-3">
        <FormField
          label={t('pay_run.line.adjustment_label')}
          htmlFor="pay-run-line-adjustment"
          helpText={t('pay_run.line.adjustment_help')}
        >
          <MoneyInput
            id="pay-run-line-adjustment"
            value={adjustmentCents}
            blankZero
            onChange={setAdjustmentCents}
          />
        </FormField>

        <FormField
          label={t('pay_run.line.adjustment_reason_label')}
          htmlFor="pay-run-line-adjustment-reason"
          error={
            reasonTooLong
              ? t('pay_run.line.reason_too_long', { max: MAX_REASON_LENGTH })
              : undefined
          }
        >
          <Input
            id="pay-run-line-adjustment-reason"
            value={adjustmentReason}
            onChange={(event) => {
              setAdjustmentReason(event.target.value);
            }}
          />
        </FormField>

        <FormField
          label={t('pay_run.line.lump_sum_label')}
          htmlFor="pay-run-line-lump-sum"
          helpText={t('pay_run.line.lump_sum_help')}
        >
          <MoneyInput
            id="pay-run-line-lump-sum"
            value={lumpSumCents}
            blankZero
            onChange={(cents) => {
              // `setPayRunLineLumpSum`'s zod input is `.min(0).nullable()`.
              // Guard here so a negative typed value never reaches the
              // network — the field's own onChange can only receive a
              // parsed number, so clamp instead of reject.
              setLumpSumCents(Math.max(0, cents));
            }}
          />
        </FormField>

        {saveError === 'step_up' && (
          <p className="nv-help is-error" role="alert">
            {t('pay_run.line.save_step_up_required')}
          </p>
        )}
        {saveError === 'error' && (
          <p className="nv-help is-error" role="alert">
            {t('pay_run.line.save_error')}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            variant="primary"
            size="sm"
            disabled={!canSave}
            loading={saving}
            onClick={() => void handleSave()}
          >
            {saving ? t('pay_run.line.saving') : t('pay_run.line.save_cta')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
